import { Box, Skeleton, Stack, Typography } from "@mui/material";
import {
  Badge,
  TableColumn,
  types,
  ObjectMetadataTags,
  utils
} from "@vilocnv/allsetra-core";
import { Clock, MileageIcon, MovingArrowLeft } from "assets/icons";
import { HiddenBadgeIcon } from "components/ui/icons";
import "moment/locale/nl";
import {
  OBJECT_RIDE_MODES,
  OBJECT_RIDE_MODES_COLOR,
  formatObjectDeviceIds,
  getObjectFleetNumber,
  getObjectMileageDisplay,
  getObjectRideTotalTimeInMinutes,
  getObjectRunningHours,
  getSplittedAddress,
  getAddressDisplayInfo
} from "../helpers";
import moment from "moment";
import { round, compact } from "lodash";
import { Fragment } from "react";
import { IFeatures } from "../types";
import InactiveObjectBadge from "components/ui/InactiveObjectBadge";
import { isInactiveObject } from "../helpers/objectStatus.helpers";

//
// TABLE HEADERS
//

export function modifyIconUrl(url: string | undefined) {
  return url ? url.replace(/\/icons\/([^/]+)-/, "/icons/$1-ns-") : "";
}

export type ObjectsTableColumnId =
  | "object"
  | "fleetNumber"
  | "group"
  | "objectType"
  | "state"
  | "mileage"
  | "runningHours"
  | "deviceIds"
  | "lastReportedDriver"
  | "lastLocation"
  | "updated"
  | "installationDate";

export type ObjectsTableColumn = TableColumn<any> & {
  id: ObjectsTableColumnId;
};

export const OBJECTS_PROTECTED_COLUMN_IDS: ObjectsTableColumnId[] = ["object"];

export const OBJECTS_COLUMN_VISIBILITY_STORAGE_KEY =
  "objectsOverview.hiddenColumnIds";

export const formatObjectGroups = (
  groups?: Array<{ name?: string | null }> | null
): string => {
  if (!groups?.length) return "";
  return groups
    .map((group) => group?.name?.trim())
    .filter(Boolean)
    .join(", ");
};

export const ALL_OBJECTS_TABLE_COLUMNS = (
  translator: any,
  lang = "en",
  resolvedAddresses: any[] = [],
  resolvedAddressLoading: boolean = false
): ObjectsTableColumn[] => {
  moment.locale(lang);
  return [
    {
      id: "object",
      name: translator("tableHeading.object", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => {
        const inactive = isInactiveObject(row?.status);
        const iconSrc = modifyIconUrl(row.icon?.url);

        return (
          <Stack gap={0.75} sx={{ pointerEvents: "none", overflow: "visible" }}>
            <Stack direction={"row"} gap={1} alignItems={"flex-start"}>
              <img
                src={iconSrc}
                alt=""
                aria-hidden
                width="24px"
                height="24px"
                style={inactive ? { opacity: 0.55 } : undefined}
              />
              <Box>
                {inactive ? (
                  <Box sx={{ mt: "-24px", mb: "-4px" }}>
                    <InactiveObjectBadge variant="inline" />
                  </Box>
                ) : null}
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: inactive ? "#646D7B" : "#1D1F2B"
                  }}
                >
                  {row.name || "N/A"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "400",
                    color: "#646D7B"
                  }}
                >
                  ID {row?.aNumber || "N/A"}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        );
      },
      wrap: true,
      sortable: true,
      sortField: "name"
    },
    {
      id: "fleetNumber",
      name: translator("tableHeading.fleetNumber", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#1D1F2B",
            pointerEvents: "none"
          }}
        >
          {getObjectFleetNumber(row) || "N/A"}
        </Typography>
      ),
      wrap: true
    },
    {
      id: "group",
      name: translator("tableHeading.group", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#1D1F2B",
            pointerEvents: "none"
          }}
        >
          {formatObjectGroups(row?.groups)}
        </Typography>
      ),
      sortable: true,
      sortField: "GroupName",
      wrap: true
    },
    {
      id: "objectType",
      name: translator("tableHeading.objectType", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#1D1F2B",
            pointerEvents: "none"
          }}
        >
          {row?.objectType?.name || "N/A"}
        </Typography>
      ),
      sortable: true,
      sortField: "objectType.name",
      wrap: true
    },
    {
      id: "state",
      name: translator("tableHeading.state", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <ObjectMetadataTags
          translator={translator}
          object={row}
          direction={"row"}
          isInTable
        />
      ),
      width: "200px"
    },
    {
      id: "mileage",
      name: translator("tableHeading.mileage", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#1D1F2B",
            pointerEvents: "none"
          }}
        >
          {getObjectMileageDisplay(row) || "N/A"}
        </Typography>
      ),
      wrap: true
    },
    {
      id: "runningHours",
      name: translator("tableHeading.totalRunningHours", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#1D1F2B",
            pointerEvents: "none"
          }}
        >
          {getObjectRunningHours(row) || "N/A"}
        </Typography>
      ),
      wrap: true
    },
    {
      id: "deviceIds",
      name: translator("tableHeading.deviceIds", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#1D1F2B",
            pointerEvents: "none"
          }}
        >
          {formatObjectDeviceIds(row?.devices) || "N/A"}
        </Typography>
      ),
      wrap: true
    },
    {
      id: "lastReportedDriver",
      name: translator("tableHeading.lastReportedDriver", {
        ns: "tableHeadingsTranslation"
      }),
      cell: (row: any) => (
        <Stack
          direction={"row"}
          gap={1}
          alignItems={"center"}
          sx={{ pointerEvents: "none" }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B"
            }}
          >
            {row.lastReportedDriver ? `${row.lastReportedDriver}` : "N/A"}
          </Typography>
        </Stack>
      )
    },
    {
      id: "lastLocation",
      name: translator("tableHeading.lastLocation", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => {
        // Find the resolved address for this object
        const resolvedAddress = resolvedAddresses.find(
          (addr) =>
            addr.latitude === row.location?.latitude &&
            addr.longitude === row.location?.longitude
        );

        // Use the resolved address if found, otherwise use the existing one
        const addressToDisplay =
          row.location?.resolvedAddress || resolvedAddress?.address;

        return resolvedAddressLoading ? (
          <>
            <Skeleton variant="text" width={100} height={24} />
            <Skeleton variant="text" width={100} height={24} />
          </>
        ) : (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B",
              pointerEvents: "none"
            }}
          >
            {getSplittedAddress(0, addressToDisplay)}
            <br />
            {getSplittedAddress(1, addressToDisplay) +
              getSplittedAddress(2, addressToDisplay)}
          </Typography>
        );
      },
      wrap: true
    },
    {
      id: "updated",
      name: translator("tableHeading.updated", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => {
        return (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B",
              pointerEvents: "none"
            }}
          >
            {moment(row.lastUpdated).fromNow()}
          </Typography>
        );
      },
      sortable: true,
      sortField: "lastUpdated"
    },
    {
      id: "installationDate",
      name: translator("tableHeading.installationDate", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => {
        return (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B",
              pointerEvents: "none"
            }}
          >
            {row?.installationDate
              ? moment(row?.installationDate).format("YYYY-MM-DD HH:MM:SS")
              : "N/A"}
          </Typography>
        );
      },
      sortable: true,
      sortField: "installationDate"
    }
  ];
};

export const OBJECT_RIDES_TABLE_COLUMNS = (
  translator: any,
  showMovementRides: boolean = false,
  resolvedAddresses: any[] = [],
  resolvedAddressLoading: boolean = false,
  loadingRideIds: Set<string> = new Set()
): TableColumn<types.IObjectRide>[] => [
  {
    name: translator("tableHeading.rideType", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide & { isCorrection: boolean }) => {
      const isRowLoading = loadingRideIds.has(row.uniqueId);

      if (isRowLoading) {
        return <Skeleton variant="rounded" width={80} height={24} />;
      }

      if (row.isCorrection) {
        return (
          <Box
            sx={{
              backgroundColor: "#323946",
              border: "1px solid #76828F",
              borderRadius: "4px",
              padding: "4px 8px",
              color: "#FFFFFF",
              fontSize: "11px",
              fontWeight: 600
            }}
          >
            {translator(`rideMode.correction`)}
          </Box>
        );
      }

      return (
        <Stack spacing={2}>
          <Stack>
            {/* @ts-ignore */}
            <Badge
              colorScheme={OBJECT_RIDE_MODES_COLOR[row.rideMode] ?? "error"}
            >
              {/* @ts-ignore */}
              <div>{OBJECT_RIDE_MODES(translator)[row.rideMode] ?? "N/A"}</div>
            </Badge>
          </Stack>
        </Stack>
      );
    },
    sortable: true,
    wrap: true,
    width: "150px"
  },

  {
    name: translator("tableHeading.rideStart", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => {
      const resolvedAddress = resolvedAddresses.find(
        (addr) =>
          addr.latitude === row.startLatitude &&
          addr.longitude === row.startLongitude
      );

      const addressToDisplay = resolvedAddress?.address;

      return (
        <Stack spacing={2}>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <div>{utils.formatDate({ date: row?.startDate })}</div>
            {row?.startedWithoutIgnition && showMovementRides && (
              <MovingArrowLeft
                label={translator("common.movementRide") ?? "Movement ride"}
              />
            )}
          </Stack>
          {(() => {
            const addressInfo = getAddressDisplayInfo(
              row.startLatitude,
              row.startLongitude,
              addressToDisplay
            );

            if (addressInfo.shouldShow) {
              return (
                <div>
                  {resolvedAddressLoading ? (
                    <>
                      <Skeleton variant="text" width={100} height={18} />
                      <Skeleton variant="text" width={100} height={18} />
                    </>
                  ) : (
                    addressToDisplay || "-"
                  )}
                </div>
              );
            } else if (addressInfo.isHidden) {
              return (
                <Box mx={20}>
                  <Badge
                    colorScheme="blank"
                    icon={<HiddenBadgeIcon />}
                    textWeight="light"
                  >
                    {translator("typography.addressIsHidden")}
                  </Badge>
                </Box>
              );
            } else if (addressInfo.isUnknown) {
              return (
                <Box mx={20}>
                  <Badge colorScheme="blank" textWeight="light">
                    {translator("typography.addressUnknown")}
                  </Badge>
                </Box>
              );
            } else if (addressInfo.isNA) {
              return (
                <Box mx={20}>
                  <Badge colorScheme="blank" textWeight="light">
                    N/A
                  </Badge>
                </Box>
              );
            }
            return null;
          })()}
        </Stack>
      );
    },
    sortable: true,
    wrap: true
  },
  {
    name: translator("tableHeading.rideEnd", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => {
      const resolvedAddress = resolvedAddresses.find(
        (addr) =>
          addr.latitude === row.endLatitude &&
          addr.longitude === row.endLongitude
      );

      const addressToDisplay = resolvedAddress?.address;

      return (
        <Stack spacing={2}>
          {row.status === 1 ? (
            <Badge colorScheme={"purple"}>Ride in progress</Badge>
          ) : (
            <Fragment>
              <div>{utils.formatDate({ date: row?.endDate })}</div>
              {(() => {
                const addressInfo = getAddressDisplayInfo(
                  row.endLatitude,
                  row.endLongitude,
                  addressToDisplay
                );

                if (addressInfo.shouldShow) {
                  return (
                    <div>
                      {resolvedAddressLoading ? (
                        <>
                          <Skeleton variant="text" width={100} height={18} />
                          <Skeleton variant="text" width={100} height={18} />
                        </>
                      ) : (
                        addressToDisplay || "-"
                      )}
                    </div>
                  );
                } else if (addressInfo.isHidden) {
                  return (
                    <Box mx={20}>
                      <Badge
                        colorScheme="blank"
                        icon={<HiddenBadgeIcon />}
                        textWeight="light"
                      >
                        {translator("typography.addressIsHidden")}
                      </Badge>
                    </Box>
                  );
                } else if (addressInfo.isUnknown) {
                  return (
                    <Box mx={20}>
                      <Badge colorScheme="blank" textWeight="light">
                        {translator("typography.addressUnknown")}
                      </Badge>
                    </Box>
                  );
                } else if (addressInfo.isNA) {
                  return (
                    <Box mx={20}>
                      <Badge colorScheme="blank" textWeight="light">
                        N/A
                      </Badge>
                    </Box>
                  );
                }
                return null;
              })()}
            </Fragment>
          )}
        </Stack>
      );
    },
    sortable: true,
    wrap: true
  },
  {
    name: translator("tableHeading.tags", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => {
      const isRowLoading = loadingRideIds.has(row.uniqueId);

      if (isRowLoading) {
        return (
          <Stack spacing={2}>
            <Skeleton variant="rounded" width={70} height={24} />
            <Skeleton variant="rounded" width={70} height={24} />
          </Stack>
        );
      }

      return (
        <Stack spacing={2}>
          {row.status !== 1 ? (
            <>
              <Badge
                colorScheme={"info"}
                icon={<Clock />}
                textVariant={"small"}
              >
                {getObjectRideTotalTimeInMinutes(row)} {"min"}
              </Badge>
              <Badge
                colorScheme={"info"}
                icon={<MileageIcon />}
                textVariant={"small"}
              >
                {round(row.mileage || 0, 1)} {"km"}
              </Badge>
            </>
          ) : null}
        </Stack>
      );
    },
    sortable: true,
    width: "150px"
  },
  {
    name: translator("tableHeading.keyLabel", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => {
      const isRowLoading = loadingRideIds.has(row.uniqueId);

      if (isRowLoading) {
        return (
          <Stack spacing={0.5}>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={60} height={16} />
          </Stack>
        );
      }

      // Compute once, keep logic simple and consistent
      // @ts-ignore
      const keyLabel = row?.key?.label ?? row?.keyId ?? row?.rawKeyId ?? "N/A";
      // @ts-ignore
      const keyId = row?.key?.keyId ?? row?.keyId ?? row?.rawKeyId;

      return (
        <Stack spacing={0.5} sx={{ pointerEvents: "none" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B"
            }}
          >
            {keyLabel}
          </Typography>
          {keyId ? (
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#646D7B"
              }}
            >
              ID {keyId}
            </Typography>
          ) : null}
        </Stack>
      );
    },
    sortable: true,
    width: "150px"
  },
  {
    name: translator("tableHeading.driver", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => {
      const isRowLoading = loadingRideIds.has(row.uniqueId);

      if (isRowLoading) {
        return <Skeleton variant="text" width={100} height={20} />;
      }

      // @ts-ignore
      const driverName: string | undefined = row?.driverName;
      // @ts-ignore
      const driver: any = row?.driver;
      const composed = driver
        ? `${driver?.firstName ?? ""} ${driver?.lastName ?? ""}`.trim()
        : "";
      const fallbackEmail = driver?.email ?? "";
      const name = driverName || composed || fallbackEmail;
      return name || "N/A";
    },
    sortable: true,
    width: "180px"
  }
];

export const IgnitionColorSchemes: any = {
  On: "info",
  Off: "error",
  null: "grey",
  "N/A": "grey"
};

export const IgnitionColorValues: any = {
  On: "#0269EC",
  Off: "#CC1010",
  null: "#959EB2",
  "N/A": "#959EB2"
};

//
// TAB PANES HEADINGS
//
export const OBJECTS_DETAILS_TABS_HEADINGS = (
  translator: any,
  features: IFeatures,
  isMonitoringEnabled = false,
  isGrafanaEnabled = false
): string[] =>
  compact([
    translator("common.details"),
    features.isRidesEnabled ? translator("common.rides") : null,
    isMonitoringEnabled ? "Monitoring" : null,
    isGrafanaEnabled ? "Grafana" : null,
    translator("maintenanceAlerts.maintenanceAlertsTab")
  ]);

export const getObjectsDetailsTabRouteNames = (
  isRidesEnabled: boolean,
  isMonitoringEnabled = false,
  isGrafanaEnabled = false
): string[] =>
  compact([
    "details",
    isRidesEnabled ? "rides" : null,
    isMonitoringEnabled ? "monitoring" : null,
    isGrafanaEnabled ? "grafana" : null,
    "maintenance-alerts"
  ]);

export const getObjectsDetailsTabsIndexToRouteNameMapping = (
  isRidesEnabled: boolean,
  isMonitoringEnabled = false,
  isGrafanaEnabled = false
): Record<number, string> =>
  Object.fromEntries(
    getObjectsDetailsTabRouteNames(
      isRidesEnabled,
      isMonitoringEnabled,
      isGrafanaEnabled
    ).map((routeName, index) => [index, routeName])
  );
